import React from "react";
import { Waves, Zap, Users, Award } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import SubHero from "@/components/layout/SubHero";
import NextStep from "@/components/layout/NextStep";
import { Reveal, SectionOverline } from "@/components/landing/Shared";

const PILLARS = [
  {
    key: "regulation",
    number: "01",
    icon: Waves,
    title: "Physiological Regulation",
    subtitle: "The foundation of the framework.",
    objective:
      "To improve an individual's physiological capacity to tolerate stress and engage in change.",
    focus: [
      "Exercise and movement prescription",
      "Recovery capacity",
      "Stress modulation",
      "Sleep architecture",
      "Energy regulation",
      "Autonomic nervous system adaptation",
    ],
  },
  {
    key: "activation",
    number: "02",
    icon: Zap,
    title: "Behavioral Activation",
    subtitle: "Action is a precursor to motivation, not the result of it.",
    objective: "To create momentum through structured, repeatable action.",
    focus: [
      "Accountability structures",
      "Goal-directed behavior",
      "Routine development",
      "Habit formation",
      "Consistency strategies",
      "Progressive engagement",
    ],
  },
  {
    key: "engagement",
    number: "03",
    icon: Users,
    title: "Recreation & Meaningful Engagement",
    subtitle:
      "Recovery and wellness require more than symptom reduction.",
    objective:
      "To increase quality of life and establish meaningful alternatives to unhealthy patterns.",
    focus: [
      "Recreation therapy",
      "Community participation",
      "Social connection",
      "Leisure development",
      "Identity exploration",
      "Purposeful activity",
    ],
  },
  {
    key: "performance",
    number: "04",
    icon: Award,
    title: "Adaptive Performance",
    subtitle: "The culmination of the framework.",
    objective:
      "To develop the skills and physiological capacity necessary for long-term self-regulation.",
    focus: [
      "Resilience",
      "Self-efficacy",
      "Functional independence",
      "Confidence and identity",
      "Long-term maintenance",
      "Sustainable lifestyle practices",
    ],
  },
];

const Framework = () => {
  return (
    <PageShell>
      <SubHero
        testId="framework-page-hero"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Methodology", to: "/methodology" },
          { label: "The Framework" },
        ]}
        overline="The Framework"
        headline="Four pillars."
        headlineAccent="One retrained system."
        lead="Adaptive Physiology™ is delivered through four interlocking pillars. Each pillar produces measurable signal, and each signal informs the next prescription — closing the loop."
      />

      <section
        data-testid="framework-pillars-section"
        className="relative py-24 sm:py-32"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="space-y-8">
            {PILLARS.map((p, i) => (
              <Reveal delay={i * 0.06} key={p.key}>
                <article
                  data-testid={`framework-pillar-${p.key}`}
                  className="rounded-[24px] border border forr-surface hover:border-teal-soft transition-colors duration-500 overflow-hidden"
                >
                  <div className="grid grid-cols-12 gap-8 p-8 sm:p-12">
                    <div className="col-span-12 md:col-span-5">
                      <div className="flex items-center gap-4 mb-6">
                        <span className="font-display forr-text-teal-deep text-3xl font-medium tracking-tight">
                          {p.number}
                        </span>
                        <div className="w-10 h-10 rounded-2xl bg-teal-tint border border-teal-soft flex items-center justify-center forr-text-teal-deep">
                          <p.icon size={18} strokeWidth={1.5} />
                        </div>
                      </div>
                      <h2 className="font-display forr-text-ink text-2xl sm:text-3xl md:text-4xl font-medium tracking-[-0.02em] leading-[1.08]">
                        {p.title}
                      </h2>
                      <p className="mt-4 forr-text-soft font-light leading-relaxed">
                        {p.subtitle}
                      </p>
                    </div>

                    <div className="col-span-12 md:col-span-7 md:pl-8 md:border-l md:border">
                      <div className="text-[11px] uppercase tracking-[0.28em] forr-text-mute mb-3">
                        Objective
                      </div>
                      <p className="text-lg forr-text-ink font-light leading-relaxed">
                        {p.objective}
                      </p>

                      <div className="mt-10 text-[11px] uppercase tracking-[0.28em] forr-text-mute mb-4">
                        Focus Areas
                      </div>
                      <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                        {p.focus.map((f) => (
                          <li
                            key={f}
                            className="text-sm forr-text-soft font-light py-1.5 border-b border flex items-center gap-3"
                          >
                            <span className="w-1 h-1 rounded-full bg-teal-400" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section
        data-testid="framework-loop-section"
        className="relative py-24 sm:py-32 border-t border"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-5">
              <Reveal>
                <SectionOverline
                  number="05"
                  label="The Loop"
                  testId="framework-loop-overline"
                />
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-8 font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
                  Interlocking, not sequential.
                </h2>
              </Reveal>
            </div>
            <div className="col-span-12 lg:col-span-7 lg:pl-8">
              <Reveal delay={0.1}>
                <p className="text-lg font-light forr-text-soft leading-relaxed">
                  The four pillars are not a linear progression. They operate
                  concurrently — each producing signal that informs how the others
                  are delivered.
                </p>
                <p className="mt-6 text-lg font-light forr-text-soft leading-relaxed">
                  Regulation improves engagement. Engagement reinforces activation.
                  Activation compounds into performance. Performance protects
                  regulation. The loop closes — and the physiology adapts.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <NextStep
        overline="Next"
        title="The Science"
        description="The physiological mechanisms and research base behind the four pillars — HRV, BDNF, autonomic tone, and beyond."
        to="/methodology/science"
        cta="Read the science"
        testId="framework-next-step"
      />
    </PageShell>
  );
};

export default Framework;
